/**
 * Single owner of "which conversation is on screen" for the chat screen
 * (de-slop CHAT-3 / CHAT-4 / CHAT-5 / CHAT-15).
 *
 * The screen used to spread this across `isStartingNewRef`,
 * `loadingSessionIdRef` and `latestSessionIdRef`. The last one was synced in
 * an effect (a render late), and `loadMessages` wrote `loadingSessionIdRef`
 * and then compared it with itself, so a reply for session A could be painted
 * while B was on screen, and an attach in "New chat" mode left the
 * starting-new flag set so the next message went to a second session.
 *
 * Pure module (no React import) so it can be unit-tested; the screen keeps one
 * instance in a ref and updates it synchronously next to every
 * `setCurrentSessionId`.
 */
export interface ConversationGuard {
  /** Session currently shown, or null (empty state / "New chat"). */
  readonly viewSessionId: number | null;
  /** True after "New chat" until a session is created or selected. */
  readonly startingNew: boolean;
  /** Show this session (user pick, deep link, or a session a flow created). */
  select(sessionId: number | null): void;
  /** Enter "New chat" mode: nothing may load into the view until a select(). */
  startNew(): void;
  /** True when results for `sessionId` belong on screen right now. */
  isViewing(sessionId: number | null | undefined): boolean;
  /** Start a message load for `sessionId`; returns a token. */
  beginLoad(sessionId: number): number;
  /** Mark a load finished (success or failure). */
  finishLoad(token: number): void;
  /** Apply a finished load only if it is the newest for a session still on screen. */
  shouldApplyLoad(token: number, sessionId: number): boolean;
  /** Whether a load is in flight (for `sessionId`, or for any session). */
  isLoading(sessionId?: number | null): boolean;
}

export function createConversationGuard(): ConversationGuard {
  let viewSessionId: number | null = null;
  let startingNew = false;
  let loadSeq = 0;
  const latestLoadBySession = new Map<number, number>();
  const inFlight = new Map<number, number>(); // token -> sessionId

  const isViewing = (sessionId: number | null | undefined) =>
    sessionId != null && !startingNew && sessionId === viewSessionId;

  return {
    get viewSessionId() {
      return viewSessionId;
    },
    get startingNew() {
      return startingNew;
    },
    select(sessionId) {
      viewSessionId = sessionId;
      startingNew = false;
    },
    startNew() {
      viewSessionId = null;
      startingNew = true;
    },
    isViewing,
    beginLoad(sessionId) {
      loadSeq += 1;
      latestLoadBySession.set(sessionId, loadSeq);
      inFlight.set(loadSeq, sessionId);
      return loadSeq;
    },
    finishLoad(token) {
      inFlight.delete(token);
    },
    shouldApplyLoad(token, sessionId) {
      return latestLoadBySession.get(sessionId) === token && isViewing(sessionId);
    },
    isLoading(sessionId) {
      if (sessionId == null) return inFlight.size > 0;
      for (const id of inFlight.values()) {
        if (id === sessionId) return true;
      }
      return false;
    },
  };
}

/**
 * Session a send should go to. "New chat" (or a user with no sessions at all)
 * needs a new session; otherwise the one on screen, falling back to the most
 * recent session.
 */
export function resolveSendTarget(
  guard: Pick<ConversationGuard, 'startingNew' | 'viewSessionId'>,
  mostRecentSessionId: number | null,
): { kind: 'create' } | { kind: 'existing'; sessionId: number } {
  if (guard.startingNew) return { kind: 'create' };
  if (guard.viewSessionId != null) return { kind: 'existing', sessionId: guard.viewSessionId };
  if (mostRecentSessionId != null) return { kind: 'existing', sessionId: mostRecentSessionId };
  return { kind: 'create' };
}
